import {
    ALLOW_NEARBY_IS_ACTIVE_DEFAULT,
    DIRECT_ONLY_IS_ACTIVE_DEFAULT,
    STATION_IS_ACTIVE_DEFAULT,
    SORT_TYPES_ARRAY,
    DEFAULT_SORT,
} from '../constants';

function expandFilterSet(
    filterSet,
    carrierModeMap,
    allTravelModes = [],
    carriersByMode,
    stations = { arrival: {}, departure: {} }
) {
    const {
        travelModes: travelModeMap,
        carriers: carrierMap,
        timing: timingMap,
        allowNearby,
        directOnly,
        features,
        sortType,
        stations: stationMap,
    } = filterSet;
    const expandedTravelModes = _expandTravelModes(
        travelModeMap,
        carrierMap,
        allTravelModes,
        carriersByMode,
    );

    return {
        travelModes: expandedTravelModes,
        carriers: _expandCarriers(carrierMap, expandedTravelModes, carrierModeMap),
        timing: _expandTimingMap(timingMap),
        allowNearby: _expandAllowNearby(allowNearby),
        directOnly: _expandDirectOnly(directOnly),
        features: _expandFeatures(features),
        sortType: _expandSortType(sortType),
        stations: _expandStations(stations, stationMap),
    };
}

function _expandTravelModes(travelModeMap, carrierMap, allTravelModes, carriersByMode) {
    const travelModes = [...new Set([...allTravelModes, ...Object.keys(travelModeMap)])];

    return travelModes.reduce((obj, travelMode) => ({
        ...obj,
        [travelMode]: _computeTravelModeState(
            travelMode,
            travelModeMap,
            carrierMap,
            carriersByMode,
        ),
    }), {});
}

function _computeTravelModeState(travelMode, travelModeMap = {}, carrierMap = {}, carriersByMode) {
    let state = travelModeMap[travelMode];
    const carriers = carriersByMode[travelMode];
    // If other travel modes are defined, then only they are active
    const hasActiveTravelMode = _hasActiveTravelMode(travelModeMap);
    if (state === undefined && hasActiveTravelMode) {
        state = false;
    }

    // Check false case first as it overrides all other notation
    if (state === false) {
        return false;
    }

    const allCarriersInactive = _allCarriersAreInactive(travelMode, carrierMap, carriers);
    if (allCarriersInactive) {
        return false;
    }

    // The undefined or true case can be modified to mixed given carrier notation
    const isMixed = _hasDefinedCarrierByMode(travelMode, carrierMap, carriers);
    if (isMixed) {
        return 'mixed';
    }

    // Otherwise undefined is defaulted to true
    return true;
}

function _allCarriersAreInactive(travelModeToCheck, carrierMap, carriers) {
    if (!carriers) {
        return false;
    }

    return carriers.every((carrierCode) => {
        if (!carrierMap.hasOwnProperty(carrierCode)) {
            return false;
        }

        const carrierState = carrierMap[carrierCode][travelModeToCheck];
        const carrierAllState = carrierMap[carrierCode].all;
        return carrierState === false || carrierAllState === false;
    });
}

function _hasActiveTravelMode(travelModeMap) {
    return Object.values(travelModeMap).some((state) => state !== false);
}

function _hasDefinedCarrierByMode(travelModeToCheck, carrierMap, carriers) {
    if (!carriers) {
        return false;
    }

    return carriers.some((carrierCode) => {
        if (!carrierMap.hasOwnProperty(carrierCode)) {
            return false;
        }

        const carrierState = carrierMap[carrierCode][travelModeToCheck];
        const carrierAllState = carrierMap[carrierCode].all;
        return carrierState !== undefined || carrierAllState !== undefined;
    });
}

function _expandCarriers(carrierMap, travelModeMap, carrierModeMap) {
    const allCarriers = [...new Set([...Object.keys(carrierModeMap), ...Object.keys(carrierMap)])];

    return allCarriers.reduce((map, carrierCode) => {
        const modes = carrierModeMap[carrierCode] || [];
        const carrierTravelModeMap = carrierMap[carrierCode] || {};
        const carrierModes = Object.keys(carrierTravelModeMap);
        const allModes = [...new Set([...carrierModes, ...modes, 'all'])];

        const newCarrierTravelModeMap = allModes.reduce((map, travelMode) => {
            const carrierValue = carrierTravelModeMap[travelMode];

            if (travelMode === 'all') {
                if (carrierValue !== undefined) {
                    return {
                        ...map,
                        [travelMode]: carrierValue,
                    };
                }

                return map;
            }

            const state = _computeCarrierState(carrierCode, travelMode, carrierMap, travelModeMap);
            return {
                ...map,
                [travelMode]: state,
            };
        }, {});

        return {
            ...map,
            [carrierCode]: newCarrierTravelModeMap,
        };
    }, {});
}

function _computeCarrierState(carrierCode, travelMode, carrierMap, travelModeMap) {
    const carrierTravelModeMap = carrierMap[carrierCode] || {};
    const carrierValue = carrierTravelModeMap[travelMode];

    if (carrierValue !== undefined) {
        return carrierValue;
    }

    const travelModeValue = travelModeMap[travelMode];
    if (typeof travelModeValue === 'boolean') {
        return travelModeValue;
    }

    const allValue = carrierTravelModeMap.all;
    if (allValue !== undefined) {
        return allValue;
    }

    const hasActiveCarrier = _hasActiveCarrierByMode(travelMode, carrierMap);
    if (hasActiveCarrier) {
        return false;
    }

    return true;
}

function _hasActiveCarrierByMode(travelModeToCheck, carrierMap) {
    return Object.entries(carrierMap).some(([carrierCode, carrierTravelModeMap]) => {
        return Object.entries(carrierTravelModeMap).some(([travelMode, isActive]) => {
            if (travelMode !== 'all' && travelMode !== travelModeToCheck) {
                return false;
            }

            if (travelMode === 'all' && isActive !== undefined) {
                if (carrierTravelModeMap[travelModeToCheck] === undefined) {
                    return isActive;
                }

                return false;
            }

            if (isActive !== undefined) {
                return isActive;
            }

            return false;
        });
    });
}

function _expandTimingMap(timingMap = []) {
    const [
        departTimingMap = [],
        arriveTimingMap = [],
    ] = timingMap;

    return {
        departure: [
            departTimingMap[0] || 0,
            departTimingMap[1] || 24,
        ],
        arrival: [
            arriveTimingMap[0] || 0,
            arriveTimingMap[1] || 24,
        ],
    };
}

function _expandAllowNearby(allowNearby) {
    return allowNearby === undefined
        ? ALLOW_NEARBY_IS_ACTIVE_DEFAULT
        : allowNearby;
}

function _expandDirectOnly(directOnly) {
    return directOnly === undefined
        ? DIRECT_ONLY_IS_ACTIVE_DEFAULT
        : directOnly;
}

// TODO should we expand this?
function _expandFeatures(features) {
    return features;
}

function _expandStationMap(stationList, stationMap = {}) {
    return Object.keys(stationList).reduce((map, stationId) => {
        const isActive = stationMap[stationId] !== undefined
            ? stationMap[stationId]
            : STATION_IS_ACTIVE_DEFAULT;
        return {
            ...map,
            [stationId]: isActive,
        };
    }, {});
}

// TODO should we expand this?
function _expandStations(stations, stationsMap) {
    const {
        arrival,
        departure,
    } = stations;
    const {
        arrival: arriveMap,
        departure: departMap,
    } = stationsMap;

    const expandedArrive = _expandStationMap(arrival, arriveMap);
    const expandedDepart = _expandStationMap(departure, departMap);
    return {
        arrival: expandedArrive,
        departure: expandedDepart,
    };
}

function _expandSortType(sortType) {
    if (!SORT_TYPES_ARRAY.includes(sortType)) {
        return DEFAULT_SORT;
    }

    return sortType;
}

/*
    Rules:
    1) If travel mode has any active carriers, undefined carriers is false
    2) Mixed state = travel mode is false and some carriers are true
*/
function minimizeFilterSet(filterSet) {
    if (!filterSet) {
        return;
    }

    const {
        travelModes: travelModeMap,
        carriers: carrierMap,
        timing: timingMap,
        allowNearby,
        directOnly,
        features,
        sortType,
        stations,
    } = filterSet;

    return {
        travelModes: _compressTravelModes(travelModeMap),
        carriers: _compressCarriers(carrierMap, travelModeMap),
        timing: _compressTimingMap(timingMap),
        allowNearby: _compressAllowNearby(allowNearby),
        directOnly: _compressDirectOnly(directOnly),
        features: _compressFeatures(features),
        sortType: _compressSortType(sortType),
        stations: _compressStations(stations),
    };
}

function _compressTravelModes(travelModeMap) {
    return _minimizeActiveTravelModeNotation(travelModeMap);
}

function _minimizeActiveTravelModeNotation(travelModeMap) {
    const allTravelModesActive = _allTravelModesAreActive(travelModeMap);
    if (allTravelModesActive) {
        return {};
    }

    const hasActiveTravelMode = _hasActiveTravelMode(travelModeMap);
    if (hasActiveTravelMode) {
        const activeTravelModes = _getActiveTravelModes(travelModeMap);
        const inactiveTravelModes = _getInctiveTravelModes(travelModeMap);
        if (activeTravelModes.length <= inactiveTravelModes.length) {
            return Object.entries(travelModeMap).reduce((map, [travelMode, state]) => {
                if (state === false) {
                    return map;
                }

                return {
                    ...map,
                    [travelMode]: true,
                };
            }, {});
        }

        return Object.entries(travelModeMap).reduce((map, [travelMode, state]) => {
            if (state !== false) {
                return map;
            }

            return {
                ...map,
                [travelMode]: false,
            };
        }, {});
    }

    return travelModeMap;
}

function _getActiveTravelModes(travelModeMap) {
    return Object.values(travelModeMap).filter((state) => state !== false);
}

function _getInctiveTravelModes(travelModeMap) {
    return Object.values(travelModeMap).filter((state) => state === false);
}

function _allTravelModesAreActive(travelModeMap) {
    return Object.values(travelModeMap).every((state) => state === true);
}

function _allTravelModesAreTruthy(travelModeMap) {
    return Object.values(travelModeMap).every((state) => state === true || state === 'mixed');
}

function _compressCarriers(carrierMap, travelModeMap) {
    carrierMap = _minimizeActiveCarrierNotation(carrierMap, travelModeMap);
    carrierMap = _compressCarriersByTravelModes(carrierMap, travelModeMap);
    carrierMap = _compressCarrierNotationByAll(carrierMap);
    carrierMap = _removeUnnecessaryCarrierNotation(carrierMap, travelModeMap);
    return carrierMap;
}

function _minimizeActiveCarrierNotation(carrierMap, travelModeMap) {
    const activeCarriersByModeMap = Object.keys(travelModeMap).reduce((map, travelMode) => {
        return {
            ...map,
            [travelMode]: {
                active: _getAllActiveCarriersByMode(travelMode, carrierMap),
                inactive: _getAllInactiveCarriersByMode(travelMode, carrierMap),
            },
        };
    }, {});

    return Object.entries(carrierMap).reduce((map, [carrierCode, carrierTravelMap]) => {
        const newCarrierTravelModeMap = Object.entries(carrierTravelMap)
            .reduce((map, [travelMode, isActive]) => {
                if (travelMode === 'all') {
                    return {
                        ...map,
                        [travelMode]: isActive,
                    };
                }

                if (!travelModeMap.hasOwnProperty(travelMode)) {
                    return {
                        ...map,
                        [travelMode]: isActive,
                    };
                }

                const activeCarriers = activeCarriersByModeMap[travelMode].active;
                const inactiveCarriers = activeCarriersByModeMap[travelMode].inactive;
                const hasMoreActiveCarriers = activeCarriers.length > inactiveCarriers.length;
                const hasActiveCarriers = activeCarriers.length > 0;
                const allState = carrierTravelMap.all;
                const hasAllState = allState !== undefined;
                const conflictsWithAllState = hasAllState && allState !== isActive;

                if (hasMoreActiveCarriers) {
                    if (isActive && !conflictsWithAllState) {
                        return {
                            ...map,
                            [travelMode]: undefined,
                        };
                    }

                    return {
                        ...map,
                        [travelMode]: false,
                    };
                }

                if (hasActiveCarriers) {
                    if (isActive) {
                        return {
                            ...map,
                            [travelMode]: true,
                        };
                    }

                    return {
                        ...map,
                        [travelMode]: undefined,
                    };
                }

                return {
                    ...map,
                    [travelMode]: isActive,
                };
            }, {});

        return {
            ...map,
            [carrierCode]: newCarrierTravelModeMap,
        };
    }, {});
}

function _getAllActiveCarriersByMode(travelMode, carrierMap) {
    return Object.keys(carrierMap)
        .filter((carrierCode) => carrierMap[carrierCode][travelMode] === true);
}

function _getAllInactiveCarriersByMode(travelMode, carrierMap) {
    return Object.keys(carrierMap)
        .filter((carrierCode) => carrierMap[carrierCode][travelMode] === false);
}

function _compressCarrierNotationByAll(carrierMap) {
    Object.entries(carrierMap).forEach(([carrierCode, carrierTravelModeMap]) => {
        const allValue = carrierTravelModeMap.all;

        if (allValue === true) {
            return carrierMap[carrierCode] = Object.entries(carrierTravelModeMap)
                .reduce((map, [travelMode, isActive]) => {
                    if (travelMode !== 'all' && isActive) {
                        return map;
                    }

                    return {
                        ...map,
                        [travelMode]: isActive,
                    };
                }, {});
        }

        if (allValue === false) {
            return carrierMap[carrierCode] = Object.entries(carrierTravelModeMap)
                .reduce((map, [travelMode, isActive]) => {
                    if (travelMode !== 'all' && !isActive) {
                        return map;
                    }

                    return {
                        ...map,
                        [travelMode]: isActive,
                    };
                }, {});
        }
    });
    return carrierMap;
}

function _removeUnnecessaryCarrierNotation(carrierMap) {
    return Object.entries(carrierMap).reduce((map, [carrierCode, carrierTravelModeMap]) => {
        const allModesAreUndefined = _allModesAreUndefined(carrierTravelModeMap);
        if (allModesAreUndefined) {
            return map;
        }

        const newCarrierTravelModeMap = Object.entries(carrierTravelModeMap)
            .reduce((map, [travelMode, isActive]) => {
                if (isActive === undefined) {
                    return map;
                }

                return {
                    ...map,
                    [travelMode]: isActive,
                };
            }, {});

        return {
            ...map,
            [carrierCode]: newCarrierTravelModeMap,
        };
    }, {});
}

function _allModesAreUndefined(carrierTravelModeMap) {
    return Object.values(carrierTravelModeMap)
        .every((travelMode) => travelMode === undefined);
}

function _compressCarriersByTravelModes(carrierMap, travelModeMap) {
    return Object.entries(carrierMap).reduce((obj, [carrierCode, carrierTravelModeMap]) => {
        const newCarrierTravelModeMap = Object.entries(carrierTravelModeMap)
            .reduce((obj, [travelMode, isActive]) => {
                if (travelMode === 'all') {
                    if (isActive !== undefined) {
                        return {
                            ...obj,
                            [travelMode]: isActive,
                        };
                    }

                    return obj;
                }

                const travelModeValue = travelModeMap[travelMode];
                if (travelModeValue === 'mixed') {
                    const hasActiveCarrier = _hasActiveCarrierByMode(travelMode, carrierMap);
                    if (hasActiveCarrier && isActive === false) {
                        return obj;
                    }

                    return {
                        ...obj,
                        [travelMode]: isActive,
                    };
                }

                const travelModeIsActive = travelModeMap[travelMode];
                if (isActive === travelModeIsActive) {
                    return obj;
                }

                return {
                    ...obj,
                    [travelMode]: isActive,
                };
            }, {});

        return {
            ...obj,
            [carrierCode]: newCarrierTravelModeMap,
        };
    }, {});
}

function _compressTimingMap(timingMap) {
    const compressedTimingMap = [
        timingMap.departure,
        timingMap.arrival,
    ].map(([directionMin, directionMax]) => {
        const compressedMin = _compressMin(directionMin);
        const compressedMax = _compressMax(directionMax);

        if (compressedMin === undefined && compressedMax === undefined) {
            return undefined;
        }

        if (compressedMax === undefined) {
            return [compressedMin];
        }

        return [
            compressedMin,
            compressedMax,
        ];
    });

    const allUndefined = compressedTimingMap.every((timingMap) => !timingMap);
    if (allUndefined) {
        return;
    }

    return compressedTimingMap;
}

function _compressMin(min) {
    if (min === 0) {
        return;
    }

    return min;
}

function _compressMax(max) {
    if (max === 24) {
        return;
    }

    return max;
}

function _compressAllowNearby(allowNearby) {
    if (allowNearby === ALLOW_NEARBY_IS_ACTIVE_DEFAULT) {
        return;
    }

    return allowNearby;
}

function _compressDirectOnly(directOnly) {
    if (directOnly === DIRECT_ONLY_IS_ACTIVE_DEFAULT) {
        return;
    }

    return directOnly;
}

function _compressFeatures(features) {
    return Object.entries(features).reduce((map, [featureId, isActive]) => {
        if (!isActive) {
            return map;
        }

        return {
            ...map,
            [featureId]: isActive,
        };
    }, {});
}

function _compressStationType(station) {
    return Object.entries(station).reduce((map, [stationId, isActive]) => {
        if (isActive) return map;
        return {
            ...map,
            [stationId]: isActive,
        };
    }, {});
}

function _compressStations(stations = {}) {
    const { arrival = {}, departure = {} } = stations;
    const compressedArrive = _compressStationType(arrival) || {};
    const compressedDepart = _compressStationType(departure) || {};
    return {
        arrival: compressedArrive,
        departure: compressedDepart,
    };
}

function _compressSortType(sortType) {
    if (sortType === DEFAULT_SORT) {
        return;
    }

    return sortType;
}

export {
    expandFilterSet,
    minimizeFilterSet,
};
